import { useState, useMemo } from "react";
import { IconCheck, IconCopy } from "../";
import type { ObjectionRebuttal, FontSize } from "../../types/caseView";
import { getFontSizeClasses } from "../../utils/caseViewHelpers";
import { IconExclamationTriangle } from "../CaseView/SectionIcon";
import {
  shortenObjection,
  categorizeObjection,
  OBJECTION_CATEGORIES,
  type EnrichedObjection,
} from "../../utils/objectionIdentity";

// ============================================
// TYPES
// ============================================

type RebuttalView = "one_liner" | "bullets" | "full";

// ============================================
// VARIANT TOGGLE
// ============================================

const VARIANT_OPTIONS: { key: RebuttalView; label: string }[] = [
  { key: "one_liner", label: "One-liner" },
  { key: "bullets", label: "Bullets" },
  { key: "full", label: "Full" },
];

function VariantToggle({
  selected,
  onChange,
  callMode,
}: {
  selected: RebuttalView;
  onChange: (view: RebuttalView) => void;
  callMode: boolean;
}) {
  return (
    <div
      className={`inline-flex rounded-lg p-0.5 ${
        callMode ? "bg-slate-800" : "bg-slate-100"
      }`}
    >
      {VARIANT_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
            selected === opt.key
              ? callMode
                ? "bg-teal-600 text-white shadow-sm"
                : "bg-white text-slate-800 shadow-sm"
              : callMode
                ? "text-slate-500 hover:text-slate-300"
                : "text-slate-400 hover:text-slate-600"
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ============================================
// COPY BUTTON
// ============================================

function RebuttalCopyButton({
  text,
  label = "Copy",
  callMode,
}: {
  text: string;
  label?: string;
  callMode: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all ${
        copied
          ? callMode
            ? "bg-green-500/30 text-green-300"
            : "bg-green-100 text-green-700"
          : callMode
            ? "bg-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-600"
            : "bg-slate-100 text-slate-500 hover:text-slate-700 hover:bg-slate-200"
      }`}
    >
      {copied ? (
        <>
          <IconCheck className="w-3 h-3" />
          Copied
        </>
      ) : (
        <>
          <IconCopy className="w-3 h-3" />
          {label}
        </>
      )}
    </button>
  );
}

// ============================================
// PERSONALIZATION BADGE
// ============================================

function PersonalizationBadge({
  isPersonalized,
  callMode,
}: {
  isPersonalized: boolean;
  callMode: boolean;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider ${
        isPersonalized
          ? callMode
            ? "bg-emerald-500/20 text-emerald-300"
            : "bg-emerald-100 text-emerald-700"
          : callMode
            ? "bg-slate-700 text-slate-500"
            : "bg-slate-100 text-slate-400"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          isPersonalized
            ? callMode
              ? "bg-emerald-400"
              : "bg-emerald-500"
            : callMode
              ? "bg-slate-600"
              : "bg-slate-300"
        }`}
      />
      {isPersonalized ? "Personalized" : "Generic"}
    </span>
  );
}

// ============================================
// REBUTTAL CONTENT RENDERER
// ============================================

function RebuttalContent({
  pair,
  view,
  callMode,
  fontClass,
}: {
  pair: ObjectionRebuttal;
  view: RebuttalView;
  callMode: boolean;
  fontClass: string;
}) {
  const variants = pair.variants;

  // Graceful fallback for legacy data without variants
  if (!variants) {
    return (
      <p
        className={`${fontClass} leading-relaxed ${
          callMode ? "text-slate-200" : "text-slate-700"
        }`}
      >
        {pair.rebuttal
          .replace(/interqual/gi, "clinical guidelines")
          .replace(/mcg/gi, "evidence-based standards")
          .replace(/milliman/gi, "clinical standards")}
      </p>
    );
  }

  // Clean proprietary references
  const clean = (text: string) =>
    text
      .replace(/interqual/gi, "clinical guidelines")
      .replace(/mcg/gi, "evidence-based standards")
      .replace(/milliman/gi, "clinical standards");

  switch (view) {
    case "one_liner":
      return (
        <p
          className={`text-[15px] font-semibold leading-snug ${
            callMode ? "text-white" : "text-slate-900"
          }`}
        >
          {clean(variants.one_liner || pair.rebuttal.slice(0, 160))}
        </p>
      );

    case "bullets":
      const bullets =
        variants.bullets.length > 0
          ? variants.bullets
          : pair.rebuttal
              .split(/(?<=[.!?])\s+/)
              .filter((s) => s.trim().length > 5)
              .slice(0, 5);
      return (
        <ul className="space-y-2">
          {bullets.map((bullet, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span
                className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  callMode ? "bg-teal-400" : "bg-teal-500"
                }`}
              />
              <span
                className={`${fontClass} leading-relaxed ${
                  callMode ? "text-slate-200" : "text-slate-700"
                }`}
              >
                {clean(bullet)}
              </span>
            </li>
          ))}
        </ul>
      );

    case "full":
      return (
        <p
          className={`${fontClass} leading-relaxed ${
            callMode ? "text-slate-200" : "text-slate-700"
          }`}
        >
          {clean(variants.full_paragraph || pair.rebuttal)}
        </p>
      );

    default:
      return null;
  }
}

// ============================================
// GET COPY TEXT FOR CURRENT VIEW
// ============================================

function getCopyText(pair: ObjectionRebuttal, view: RebuttalView): string {
  if (!pair.variants) return pair.rebuttal;

  switch (view) {
    case "one_liner":
      return pair.variants.one_liner || pair.rebuttal.slice(0, 160);
    case "bullets":
      return pair.variants.bullets.length > 0
        ? pair.variants.bullets.map((b) => `• ${b}`).join("\n")
        : pair.rebuttal;
    case "full":
      return pair.variants.full_paragraph || pair.rebuttal;
    default:
      return pair.rebuttal;
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

export function DecisionTreeRebuttals({
  objectionPairs,
  callMode,
  fontSize,
  procedureName,
  caseContext,
}: {
  objectionPairs: ObjectionRebuttal[];
  callMode: boolean;
  fontSize: FontSize;
  procedureName?: string;
  caseContext?: {
    serviceName: string;
    serviceType?: "imaging" | "procedure" | "medication" | "dme" | "level_of_care" | "therapy" | "other";
    clinicalProblem: string;
    patientAge?: string;
    patientSex?: string;
    duration?: string;
    keyFindings: string[];
    failedTreatments: string[];
    redFlags: string[];
  };
}) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const [showEscalation, setShowEscalation] = useState(false);
  // Default: bullets in call mode, full in normal/appeal mode
  const [activeView, setActiveView] = useState<RebuttalView>(
    callMode ? "bullets" : "full"
  );
  const fontClass = getFontSizeClasses(fontSize, callMode);

  // Group objections by category — use enriched category if available
  const groupedObjections = useMemo(() => {
    const groups: Record<string, { pair: ObjectionRebuttal; index: number }[]> =
      {};

    objectionPairs.forEach((pair, index) => {
      const enriched = pair as EnrichedObjection;
      const category = enriched.category || categorizeObjection(pair.objection);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push({ pair, index });
    });

    return groups;
  }, [objectionPairs]);

  const handleSelect = (index: number) => {
    if (selectedIndex === index) {
      setSelectedIndex(null);
      setShowEscalation(false);
    } else {
      setSelectedIndex(index);
      setLastSelectedIndex(index);
      setShowEscalation(false);
    }
  };

  const handleClose = () => {
    setSelectedIndex(null);
    setShowEscalation(false);
  };

  const selectedPair =
    selectedIndex !== null ? objectionPairs[selectedIndex] : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <p
        className={`text-[11px] uppercase tracking-wider font-medium ${
          callMode ? "text-slate-500" : "text-slate-400"
        }`}
      >
        Select objection → get rebuttal
      </p>

      {/* Grouped objection chips */}
      <div className="space-y-3">
        {OBJECTION_CATEGORIES.map((cat) => {
          const items = groupedObjections[cat.id];
          if (!items || items.length === 0) return null;

          return (
            <div key={cat.id}>
              <p
                className={`text-[10px] uppercase tracking-wider font-semibold mb-1.5 ${
                  callMode ? "text-slate-600" : "text-slate-400"
                }`}
              >
                {cat.label}
              </p>

              <div className="flex flex-wrap gap-1.5">
                {items.map(({ pair, index }) => {
                  const isSelected = selectedIndex === index;
                  const wasLastSelected =
                    lastSelectedIndex === index && selectedIndex === null;
                  const enriched = pair as EnrichedObjection;
                  const shortLabel = enriched.shortLabel || shortenObjection(pair.objection);
                  const chipKey = enriched.stableId || `obj_${index}`;

                  return (
                    <button
                      key={chipKey}
                      onClick={() => handleSelect(index)}
                      title={pair.objection}
                      className={`px-3 py-2 rounded-lg text-[13px] font-medium transition-all leading-tight max-w-[200px] text-left ${
                        isSelected
                          ? callMode
                            ? "bg-orange-500/40 text-orange-100 border-2 border-orange-400 shadow-lg shadow-orange-500/20"
                            : "bg-orange-100 text-orange-900 border-2 border-orange-400 shadow-md"
                          : wasLastSelected
                            ? callMode
                              ? "bg-slate-700 text-slate-200 border border-orange-500/30"
                              : "bg-orange-50 text-slate-700 border border-orange-200"
                            : callMode
                              ? "bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 hover:border-orange-500/40"
                              : "bg-white text-slate-700 border border-slate-200 hover:bg-orange-50 hover:border-orange-200"
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {isSelected && (
                          <IconCheck
                            className={`w-3.5 h-3.5 flex-shrink-0 ${
                              callMode ? "text-orange-300" : "text-orange-600"
                            }`}
                          />
                        )}
                        <span className="line-clamp-2">{shortLabel}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Expanded rebuttal panel */}
      {selectedPair && (
        <div
          className={`rounded-lg border-2 overflow-hidden ${
            callMode
              ? "bg-teal-900/30 border-teal-500/50"
              : "bg-teal-50 border-teal-300"
          }`}
        >
          {/* Header with variant toggle */}
          <div
            className={`px-4 py-2.5 border-b flex items-center justify-between gap-2 flex-wrap ${
              callMode
                ? "border-teal-500/30 bg-slate-800/60"
                : "border-teal-200 bg-teal-50"
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`text-[12px] font-semibold truncate ${
                  callMode ? "text-slate-300" : "text-slate-600"
                }`}
              >
                Rebuttal:{" "}
                <span
                  className={
                    callMode ? "text-orange-300" : "text-orange-600"
                  }
                >
                  {((selectedPair as EnrichedObjection).shortLabel) || shortenObjection(selectedPair.objection)}
                </span>
              </span>
              {selectedPair.personalization && (
                <PersonalizationBadge
                  isPersonalized={selectedPair.personalization.is_personalized}
                  callMode={callMode}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <VariantToggle
                selected={activeView}
                onChange={setActiveView}
                callMode={callMode}
              />
              <RebuttalCopyButton
                text={getCopyText(selectedPair, activeView)}
                label="Copy"
                callMode={callMode}
              />
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-3">
            {!showEscalation ? (
              <RebuttalContent
                pair={selectedPair}
                view={activeView}
                callMode={callMode}
                fontClass={fontClass}
              />
            ) : (
              <div className="space-y-2">
                <p
                  className={`text-[10px] uppercase tracking-wider font-semibold ${
                    callMode ? "text-amber-400" : "text-amber-600"
                  }`}
                >
                  If they push back
                </p>
                <p
                  className={`${fontClass} leading-relaxed ${
                    callMode ? "text-slate-200" : "text-slate-700"
                  }`}
                >
                  {selectedPair.escalation
                    ?.replace(/interqual/gi, "clinical guidelines")
                    .replace(/mcg/gi, "medical necessity standards")}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className={`px-4 py-2 border-t flex items-center justify-between ${
              callMode
                ? "border-teal-500/30 bg-slate-800/40"
                : "border-teal-200 bg-slate-50"
            }`}
          >
            <button
              onClick={handleClose}
              className={`text-xs font-medium transition-colors ${
                callMode
                  ? "text-slate-500 hover:text-slate-300"
                  : "text-slate-400 hover:text-slate-600"
              }`}
            >
              Close
            </button>

            {selectedPair.escalation && !showEscalation && (
              <button
                onClick={() => setShowEscalation(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  callMode
                    ? "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                    : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                }`}
              >
                <IconExclamationTriangle className="w-3.5 h-3.5" />
                Pushback response
              </button>
            )}

            {showEscalation && (
              <button
                onClick={() => setShowEscalation(false)}
                className={`text-xs font-medium transition-colors ${
                  callMode
                    ? "text-teal-400 hover:text-teal-300"
                    : "text-teal-600 hover:text-teal-700"
                }`}
              >
                ← Back to rebuttal
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}